import { Injectable } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { RoleUserRoleLocation } from '../../shared/models/role-user-role-location';
import { UserRoleLocation } from '../../shared/models/user-role-location';
import { DhhRoleIds } from '../models/static-lookups';
import { DhhLookupsService } from './dhh-lookups.service';

/**
 * The learner fields the location predicates need. Both DhhLearnerDto and
 * DhhLearnerWithScreeningResultDto satisfy this.
 */
export interface DhhAccessLearner {
  attendingAeaId?: string;
  attendingDistrictId?: string;
  buildingId?: string;
  learnerExited?: boolean;
}

/**
 * Single source of truth for "who may see / edit what" across the DHH / Vision module.
 *
 * Three layers combine here:
 *  1. Super Admin, which short-circuits everything.
 *  2. A DHH role, optionally scoped to AEA / district / building assignments. A role with
 *     no assignments at all is statewide for the roles listed in `statewideWhenUnassigned`,
 *     matching DhhLookupService.GetLocationsAsync and sp_dhh_learner_user_access.
 *  3. The three Deafblind granular permissions, which stand on their own: each is Super Admin
 *     assigned and grants its holder statewide access inside the Deafblind Project without a
 *     DHH role. They still grant nothing outside the Deafblind Project.
 */
@Injectable({ providedIn: 'root' })
export class DhhAccessService {
  private locationsByRole: RoleUserRoleLocation[] = [];
  private locationsLoaded = false;

  /**
   * Roles for which "assigned to no locations" means statewide rather than "no access".
   * LimitedAccessVision and DhhVisionConsultant follow their siblings DHHLimitedAccess
   * and DHHReadOnly here.
   */
  private static readonly statewideWhenUnassigned: string[] = [
    DhhRoleIds.DHHLimitedAccess,
    DhhRoleIds.DHHReadOnly,
    DhhRoleIds.LimitedAccessVision,
    DhhRoleIds.DhhVisionConsultant,
    DhhRoleIds.AchieveDataLead,
    DhhRoleIds.AchieveDataTechnician,
  ];

  constructor(
    private readonly authService: AuthService,
    private readonly dhhLookupsService: DhhLookupsService
  ) {}

  get areLocationsLoaded(): boolean {
    return this.locationsLoaded;
  }

  /** Loads (and caches) the current user's DHH role/location map. */
  loadLocations(): Observable<RoleUserRoleLocation[]> {
    return this.dhhLookupsService.getDhhUserRoleLocations().pipe(
      map((res) => res?.value ?? []),
      tap((locations) => this.setLocations(locations))
    );
  }

  /** Lets callers that already hold the cached map prime this service instead of refetching. */
  setLocations(locationsByRole: RoleUserRoleLocation[]): void {
    this.locationsByRole = locationsByRole ?? [];
    this.locationsLoaded = true;
  }

  private locationsFor(roleId: string): UserRoleLocation[] {
    return this.locationsByRole.find((x) => x.roleId === roleId)?.userRoleLocations ?? [];
  }

  /**
   * True when the user holds `roleId` and that role reaches `learner` - either through an
   * AEA / district / building assignment, or by being statewide-when-unassigned.
   */
  hasRoleForLearner(roleId: string, learner: DhhAccessLearner): boolean {
    const assignments = this.locationsFor(roleId);

    if (!assignments.length) {
      // No rows at all means either "user does not hold this role" or "holds it statewide".
      // Only the latter grants access, so confirm the role is actually held.
      return this.locationsLoaded && DhhAccessService.statewideWhenUnassigned.includes(roleId) && this.holdsRole(roleId);
    }

    return assignments.some(
      (l) =>
        l.locationUid === learner?.attendingAeaId || l.locationUid === learner?.attendingDistrictId || l.locationUid === learner?.buildingId
    );
  }

  private holdsRole(roleId: string): boolean {
    switch (roleId) {
      case DhhRoleIds.DhhAudiologist:
        return this.authService.isDhhAudiologist;
      case DhhRoleIds.DhhAudiometristGeneral:
        return this.authService.isDhhAudiometrist;
      case DhhRoleIds.DHHLimitedAccess:
        return this.authService.isDhhLimitedAccess;
      case DhhRoleIds.DHHReadOnly:
        return this.authService.isDhhReadOnly;
      case DhhRoleIds.DHHSchedulingAdministration:
        return this.authService.isDhhScheduler;
      case DhhRoleIds.LimitedAccessVision:
        return this.authService.isLimitedAccessVision;
      case DhhRoleIds.DhhVisionDeStatewide:
        return this.authService.isDhhVisionDeStatewide;
      case DhhRoleIds.DhhVisionConsultant:
        return this.authService.isDhhVisionConsultant;
      case DhhRoleIds.AchieveDataLead:
        return this.authService.isAchieveDataLead;
      case DhhRoleIds.AchieveDataTechnician:
        return this.authService.isDataTechnician;
      default:
        return false;
    }
  }

  // ---------------------------------------------------------------------------
  // Deafblind Project - page level, not learner scoped
  // ---------------------------------------------------------------------------

  /**
   * Visibility of the "DeafBlind Project" left-navigation option (#33285). The spec is
   * deliberately narrow here: "This option will only be visible to users with the DeafBlind
   * Project granular permission." DTAT and Deafblind Read-Only do NOT open the menu, even
   * though both reach the pages underneath it - see `canAccessDeafblindProject`.
   *
   * This is the menu gate only. Do not reuse it for page or field access.
   */
  get canViewDeafblindProjectMenu(): boolean {
    if (this.authService.isSuperAdmin) return true;
    return this.authService.hasDeafBlindProjectPermission;
  }

  /**
   * Access to the four Deafblind Project pages themselves. All three granular permissions
   * reach them - the spec grants DTAT edit access to DTAT and Consent Agencies, and
   * Deafblind Read-Only statewide read access to all four steppers - so this stays wider
   * than `canViewDeafblindProjectMenu`, which gates the nav option alone.
   *
   * Each permission is assignable only by a Super Admin and grants this access on its own;
   * a DHH role is not additionally required.
   */
  get canAccessDeafblindProject(): boolean {
    if (this.authService.isSuperAdmin) return true;
    return (
      this.authService.hasDeafBlindProjectPermission ||
      this.authService.hasDtatPermission ||
      this.authService.hasDeafblindReadOnlyPermission
    );
  }

  /** Deafblind Registry: DeafBlind Project edits statewide; DTAT and Read-Only view. */
  get canViewDeafblindRegistry(): boolean {
    return this.canAccessDeafblindProject;
  }

  get canEditDeafblindRegistry(): boolean {
    if (this.authService.isSuperAdmin) return true;
    return this.canAccessDeafblindProject && this.authService.hasDeafBlindProjectPermission;
  }

  /** DTAT: DTAT edits; DeafBlind Project and Read-Only view. */
  get canViewDtat(): boolean {
    return this.canAccessDeafblindProject;
  }

  get canEditDtat(): boolean {
    if (this.authService.isSuperAdmin) return true;
    return this.canAccessDeafblindProject && this.authService.hasDtatPermission;
  }

  /** Consent Agencies: both DeafBlind Project and DTAT edit; Read-Only views. */
  get canViewConsentAgencies(): boolean {
    return this.canAccessDeafblindProject;
  }

  get canEditConsentAgencies(): boolean {
    if (this.authService.isSuperAdmin) return true;
    return this.canAccessDeafblindProject && (this.authService.hasDeafBlindProjectPermission || this.authService.hasDtatPermission);
  }

  /** Deafblind Reporting: DeafBlind Project and DTAT may run reports; Read-Only may view. */
  get canViewDeafblindReporting(): boolean {
    return this.canAccessDeafblindProject;
  }

  get canRunDeafblindReports(): boolean {
    if (this.authService.isSuperAdmin) return true;
    return (
      this.canAccessDeafblindProject &&
      (this.authService.hasDeafBlindProjectPermission ||
        this.authService.hasDtatPermission ||
        this.authService.hasDeafblindReadOnlyPermission)
    );
  }

  // ---------------------------------------------------------------------------
  // Vision stepper on the Learner Hearing Dashboard
  // ---------------------------------------------------------------------------

  /**
   * Every DHH role and DHH granular permission gets at least read-only access to the
   * Vision stepper. DhhVisionConsultant is included because the spec gives it read-only
   * access to everything in the DHH module.
   */
  get canViewVisionStepper(): boolean {
    return (
      this.authService.isSuperAdmin ||
      this.authService.isDhhUser ||
      this.authService.isAchieveDataLead ||
      this.authService.isDataTechnician ||
      this.authService.hasDhhStatewideSearchPermission ||
      this.authService.hasDeafBlindProjectPermission ||
      this.authService.hasDtatPermission ||
      this.authService.hasDeafblindReadOnlyPermission
    );
  }

  /**
   * Vision stepper edit rights. DhhVisionConsultant and DHHReadOnly are read-only here;
   * DhhVisionDeStatewide edits statewide; LimitedAccessVision and the Audiologist edit
   * within their assigned locations.
   */
  canEditVisionStepper(learner: DhhAccessLearner): boolean {
    if (learner?.learnerExited) return false;
    if (this.authService.isSuperAdmin) return true;
    if (this.authService.isDhhVisionDeStatewide) return true;

    return this.hasRoleForLearner(DhhRoleIds.LimitedAccessVision, learner) || this.hasRoleForLearner(DhhRoleIds.DhhAudiologist, learner);
  }

  /**
   * DhhVisionConsultant is read-only across the module with one exception: it may add
   * notes on the Learner Hearing Dashboard.
   */
  canAddNotes(learner: DhhAccessLearner): boolean {
    if (learner?.learnerExited) return false;
    if (this.authService.isSuperAdmin) return true;
    if (this.authService.isDhhVisionDeStatewide) return true;

    return (
      this.hasRoleForLearner(DhhRoleIds.DhhVisionConsultant, learner) ||
      this.hasRoleForLearner(DhhRoleIds.LimitedAccessVision, learner) ||
      this.hasRoleForLearner(DhhRoleIds.DhhAudiologist, learner) ||
      this.hasRoleForLearner(DhhRoleIds.DHHLimitedAccess, learner) ||
      this.hasRoleForLearner(DhhRoleIds.DHHSchedulingAdministration, learner)
    );
  }

  // ---------------------------------------------------------------------------
  // Special designations on General Information & Notes
  // ---------------------------------------------------------------------------

  /**
   * The Vision special designation checkbox. The spec names one role - Limited Access Vision -
   * so this is deliberately narrower than `canEditVisionStepper`, which the Audiologist and
   * DHH/Vision DE Statewide also satisfy.
   */
  canEditVisionDesignation(learner: DhhAccessLearner): boolean {
    if (learner?.learnerExited) return false;
    if (this.authService.isSuperAdmin) return true;

    return this.hasRoleForLearner(DhhRoleIds.LimitedAccessVision, learner);
  }

  /** General LHD edit rights for the roles this spec adds. */
  canEditLhd(learner: DhhAccessLearner): boolean {
    if (learner?.learnerExited) return false;
    if (this.authService.isSuperAdmin) return true;
    if (this.authService.isDhhVisionDeStatewide) return true;
    // DhhVisionConsultant is read-only everywhere except notes.
    if (this.authService.isDhhVisionConsultant && !this.authService.isLimitedAccessVision) return false;

    return this.hasRoleForLearner(DhhRoleIds.LimitedAccessVision, learner);
  }
}
